/**
 * Orders API Module
 * Handles all order-related API calls
 */

import apiClient, { extractData } from '../api-client';

// Mảng gửi dạng lặp key (tags=a&tags=b) cho FastAPI; bỏ giá trị rỗng.
function serializeListParams(p) {
  const usp = new URLSearchParams();
  Object.entries(p || {}).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item !== null && item !== undefined && item !== '') usp.append(k, item);
      });
    } else {
      usp.append(k, v);
    }
  });
  return usp.toString();
}

export const orderApi = {
  partnerOptions() { return apiClient.get('/orders/partner-options').then(extractData); },
  handoverToPartner(orderId, data) { return apiClient.post(`/orders/${orderId}/update-status`, { ...data, status: 'HANDED_OVER_TO_PARTNER' }).then(extractData); },
  /**
   * List all orders with filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 20, max: 100)
   * @param {string} params.status - Filter by status
   * @param {number} params.customer_id - Filter by customer
   * @param {string} params.from_date - Filter from date (YYYY-MM-DD)
   * @param {string} params.to_date - Filter to date (YYYY-MM-DD)
   * @param {string} params.search - Search term
   * @returns {Promise<Object>} Paginated response with orders
   */
  list(params) {
    return apiClient.get('/orders/', {
      params,
      paramsSerializer: serializeListParams,
    }).then(extractData);
  },

  /** Số đơn ngoại lệ CTV (đã rút / từ chối SX) mà danh sách mặc định đang ẩn — cùng bộ lọc với list. */
  exceptionCounts(params) {
    return apiClient.get('/orders/exception-counts', { params, paramsSerializer: serializeListParams }).then(extractData);
  },

  /**
   * Get demo image URLs for an order by its public code (no auth).
   * @param {string} orderCode
   */
  getDemoImagesByCode(orderCode) {
    return apiClient
      .get(`/orders/demo/${encodeURIComponent(orderCode)}`)
      .then(extractData);
  },

  /**
   * Get order by ID with all details
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Order response with items and history
   */
  getById(orderId) {
    return apiClient.get(`/orders/${orderId}`).then(extractData);
  },

  /**
   * Tạo phiếu in PDF (backend render) cho 1 hoặc nhiều đơn.
   * @param {number[]} orderIds
   * @returns {Promise<{blob: Blob, filename: string, skippedOrderIds: number[]}>}
   */
  printSlip(orderIds) {
    return apiClient
      .post('/orders/print-slip', { order_ids: orderIds }, { responseType: 'blob' })
      .then((res) => {
        const disposition = res.headers['content-disposition'] || '';
        const match = disposition.match(/filename="([^"]+)"/);
        const skippedHeader = res.headers['x-skipped-order-ids'] || '';
        const skippedOrderIds = skippedHeader
          ? skippedHeader.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n))
          : [];
        return { blob: res.data, filename: match ? match[1] : 'phieu-in.pdf', skippedOrderIds };
      });
  },

  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @param {number} orderData.customer_id - Customer ID (required)
   * @param {Array} orderData.items - Order items (required)
   * @param {number} orderData.delivery_days - Delivery days
   * @param {string} orderData.discount_type - Discount type (PERCENT or AMOUNT)
   * @param {number|string} orderData.discount_value - Discount value
   * @param {number|string} orderData.tax_percent - Tax percentage
   * @param {number|string} orderData.shipping_fee - Shipping fee
   * @param {string} orderData.notes - Notes
   * @returns {Promise<Object>} Created order response
   */
  create(orderData) {
    return apiClient.post('/orders/', orderData).then(extractData);
  },

  /**
   * Update order (only DRAFT status)
   * @param {number} orderId - Order ID
   * @param {Object} orderData - Order data to update
   * @returns {Promise<Object>} Updated order response
   */
  update(orderId, orderData) {
    return apiClient.put(`/orders/${orderId}`, orderData).then(extractData);
  },

  /**
   * Nhập vải cho đơn (số mét + cân nặng). Endpoint riêng vì role "Nhân viên in"
   * được nhập vải trên mọi đơn, trong khi update() vẫn chỉ cho đơn được giao.
   * @param {number} orderId - Order ID
   * @param {Object} fabricData - { shirt_fabric_meters, pants_fabric_meters, shirt_fabric_kg, pants_fabric_kg }
   * @returns {Promise<Object>} Updated order response
   */
  updateFabric(orderId, fabricData) {
    return apiClient.patch(`/orders/${orderId}/fabric`, fabricData).then(extractData);
  },

  /**
   * Ghi chú xưởng trên đơn CTV (chỉ nội bộ tổng, không gửi sang CTV).
   * @param {number} orderId
   * @param {string} factoryNote - chuỗi rỗng = xoá
   */
  updateFactoryNote(orderId, factoryNote) {
    return apiClient.patch(`/orders/${orderId}/factory-note`, { factory_note: factoryNote }).then(extractData);
  },

  /**
   * Đổi người phụ trách đơn (thay cả danh sách; mảng rỗng = gỡ hết). Endpoint riêng
   * vì đơn CTV không sửa qua update() được nữa nhưng vẫn phải giao lại người phụ
   * trách ở mọi bước. Quyền giống update(): admin hoặc người đang phụ trách đơn.
   * @param {number} orderId - Order ID
   * @param {number[]} assigneeIds - Danh sách user id
   * @returns {Promise<Object>} Updated order response
   */
  updateAssignees(orderId, assigneeIds) {
    return apiClient.patch(`/orders/${orderId}/assignees`, { assignee_ids: assigneeIds }).then(extractData);
  },

  /**
   * Đặt/gỡ override cứng cho COD báo hãng vận chuyển. CHỈ admin (backend 403 với
   * role khác). Tách khỏi update() vì form sửa đơn thường không còn gửi
   * cod_amount trong payload — số đó dễ ôi thiu (đã lưu từ lần tạo vận đơn trước).
   * @param {number} orderId - Order ID
   * @param {number|null} override - Số COD cố định, null để gỡ (đơn về tính sống)
   * @returns {Promise<Object>} Updated order response
   */
  setCodOverride(orderId, override) {
    return apiClient.patch(`/orders/${orderId}/cod`, { override }).then(extractData);
  },

  /**
   * Delete order (DRAFT only)
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Message response
   */
  delete(orderId) {
    return apiClient.delete(`/orders/${orderId}`).then(extractData);
  },

  /**
   * Lên Market (DRAFT -> ON_MARKET). Đơn đã đủ thông tin, sẵn sàng vào luồng bán hàng.
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  goToMarket(orderId, data) {
    return apiClient.post(`/orders/${orderId}/go-to-market`, data).then(extractData);
  },

  /**
   * Chốt Market (ON_MARKET -> MARKET_CLOSED). Bắn Zalo xác nhận đơn.
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  closeMarket(orderId, data) {
    return apiClient.post(`/orders/${orderId}/close-market`, data).then(extractData);
  },

  /**
   * Gửi thủ công bộ tin nhắn Zalo "Chốt Market" cho đơn (không đổi trạng thái).
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Message response
   */
  sendZaloMessage(orderId, data) {
    return apiClient.post(`/orders/${orderId}/send-zalo-message`, data).then(extractData);
  },

  /**
   * Chuyển chờ sản xuất (MARKET_CLOSED -> WAITING_PRODUCTION)
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Updated order response
   */
  confirm(orderId, data) {
    return apiClient.post(`/orders/${orderId}/confirm`, data).then(extractData);
  },

  /**
   * Bắt đầu sản xuất (WAITING_PRODUCTION -> IN_PRODUCTION)
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  startPrint(orderId, data) {
    return apiClient.post(`/orders/${orderId}/start-print`, data).then(extractData);
  },

  /**
   * Complete production (IN_PRODUCTION -> PRODUCTION_COMPLETE)
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  completeProduction(orderId, data) {
    return apiClient.post(`/orders/${orderId}/complete-production`, data).then(extractData);
  },

  /**
   * Ship order (PRODUCTION_COMPLETE -> AWAITING_PICKUP)
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  ship(orderId, data) {
    return apiClient.post(`/orders/${orderId}/ship`, data).then(extractData);
  },

  /**
   * Đánh dấu đã lấy hàng thủ công (AWAITING_PICKUP -> PICKED_UP)
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  markPickedUp(orderId, data) {
    return apiClient.post(`/orders/${orderId}/update-status`, { status: 'PICKED_UP', notes: data?.notes }).then(extractData);
  },

  /**
   * Tạo vận đơn ở GHN (không đổi status đơn hàng).
   * Status sẽ được cập nhật qua webhook GHN.
   */
  sendToCarrier(orderId, data) {
    return apiClient.post(`/orders/${orderId}/send-to-carrier`, data).then(extractData);
  },

  syncShipping(orderId) {
    return apiClient.post(`/orders/${orderId}/sync-shipping`).then(extractData);
  },

  /**
   * Đánh dấu trạng thái vận chuyển = đã giao thủ công (chỉ đơn đã Hoàn thành).
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Updated order response
   */
  markShippingDelivered(orderId) {
    return apiClient.post(`/orders/${orderId}/mark-shipping-delivered`).then(extractData);
  },

  /**
   * Deliver order (AWAITING_PICKUP/PICKED_UP -> DELIVERED)
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  deliver(orderId, data) {
    return apiClient.post(`/orders/${orderId}/deliver`, data).then(extractData);
  },

  /**
   * Cancel order (any status -> CANCELLED)
   * @param {number} orderId - Order ID
   * @param {Object} data - Cancellation data
   * @param {string} data.reason - Cancellation reason (required)
   * @returns {Promise<Object>} Updated order response
   */
  cancel(orderId, data) {
    return apiClient.post(`/orders/${orderId}/cancel`, data).then(extractData);
  },

  /**
   * Cancel current shipment only (keeps order.status unchanged) so it can be re-shipped with another carrier
   * @param {number} orderId - Order ID
   * @param {Object} data - Cancellation data
   * @param {string} data.reason - Cancellation reason (required)
   * @returns {Promise<Object>} Updated order response
   */
  cancelShipment(orderId, data) {
    return apiClient.post(`/orders/${orderId}/cancel-shipment`, data).then(extractData);
  },

  /**
   * Reopen cancelled order (CANCELLED -> ON_MARKET, admin only)
   * @param {number} orderId - Order ID
   * @returns {Promise<Object>} Updated order response
   */
  reopen(orderId, data) {
    return apiClient.post(`/orders/${orderId}/reopen`, data).then(extractData);
  },

  markPaid(orderId) {
    return apiClient.post(`/orders/${orderId}/mark-paid`).then(extractData);
  },

  markUnpaid(orderId) {
    return apiClient.post(`/orders/${orderId}/mark-unpaid`).then(extractData);
  },

  markDeposited(orderId, depositAmount = 0) {
    return apiClient.post(`/orders/${orderId}/mark-deposited`, { deposit_amount: depositAmount }).then(extractData);
  },

  /**
   * Revert order to previous status
   * @param {number} orderId - Order ID
   * @param {Object} [data] - Optional data with notes
   * @returns {Promise<Object>} Updated order response
   */
  revert(orderId, data) {
    return apiClient.post(`/orders/${orderId}/revert`, data).then(extractData);
  },

  // `schedule`: { production_finish_at, delivery_at } (ISO có múi) khi chuyển Chờ SX.
  updateStatus(orderId, status, notes = null, sendPlan = undefined, schedule = null) {
    return apiClient.post(`/orders/${orderId}/update-status`, {
      status, notes, send_plan: sendPlan, ...(schedule || {}),
    }).then(extractData);
  },

  /** Xưởng từ chối SX đơn CTV: lý do bắt buộc + tối đa 5 ảnh (File[]) chụp chỗ sai. */
  reject(orderId, reason, files = []) {
    const formData = new FormData();
    formData.append('reason', reason);
    files.forEach((file) => formData.append('files', file));
    return apiClient.post(`/orders/${orderId}/reject`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    }).then(extractData);
  },

  startReturn(orderId, data) {
    return apiClient.post(`/orders/${orderId}/update-status`, { status: 'RETURNING', notes: data?.notes }).then(extractData);
  },

  completeReturn(orderId, data) {
    return apiClient.post(`/orders/${orderId}/update-status`, { status: 'RETURNED', notes: data?.notes }).then(extractData);
  },

  /**
   * Add item to order (DRAFT only)
   * @param {number} orderId - Order ID
   * @param {Object} itemData - Order item data
   * @param {number} itemData.product_id - Product ID (required)
   * @param {number} itemData.quantity - Quantity (required)
   * @param {number|string} itemData.unit_price - Unit price (required)
   * @param {string} itemData.notes - Notes
   * @param {Array} itemData.print_list - Print list items
   * @returns {Promise<Object>} Created order item response
   */
  addItem(orderId, itemData) {
    return apiClient.post(`/orders/${orderId}/items`, itemData).then(extractData);
  },

  /**
   * Update order item (DRAFT only)
   * @param {number} orderId - Order ID
   * @param {number} itemId - Order item ID
   * @param {Object} itemData - Item data to update
   * @returns {Promise<Object>} Updated order item response
   */
  updateItem(orderId, itemId, itemData) {
    return apiClient.put(`/orders/${orderId}/items/${itemId}`, itemData).then(extractData);
  },

  /**
   * Delete order item (DRAFT only)
   * @param {number} orderId - Order ID
   * @param {number} itemId - Order item ID
   * @returns {Promise<Object>} Message response
   */
  deleteItem(orderId, itemId) {
    return apiClient.delete(`/orders/${orderId}/items/${itemId}`).then(extractData);
  },

  /**
   * Update print list for order item
   * @param {number} orderId - Order ID
   * @param {number} itemId - Order item ID
   * @param {Array} printList - Print list items (replaces entire list)
   * @returns {Promise<Object>} Message response
   */
  updatePrintList(orderId, itemId, printList) {
    return apiClient.put(`/orders/${orderId}/items/${itemId}/print-list`, printList).then(extractData);
  },

  uploadPrintFiles(orderId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/print-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deletePrintFileByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/print-file/${encodeURIComponent(filename)}`).then(extractData);
  },

  uploadAiFiles(orderId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/ai-file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deleteAiFileByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/ai-file/${encodeURIComponent(filename)}`).then(extractData);
  },

  deleteDemoImageByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/demo-image/${encodeURIComponent(filename)}`).then(extractData);
  },

  uploadNoteImages(orderId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/note-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deleteNoteImageByName(orderId, filename) {
    return apiClient.delete(`/orders/${orderId}/note-image/${encodeURIComponent(filename)}`).then(extractData);
  },

  uploadInvoiceImage(orderId, formData) {
    return apiClient.post(`/orders/${orderId}/invoice-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(extractData);
  },

  /**
   * Ảnh danh sách in do backend vẽ — CÙNG template + cache với ảnh tn2 gửi Zalo.
   * @param {'zalo'|'a4'} layout zalo: xem trước ảnh sẽ gửi; a4: mỗi ảnh một trang PDF.
   * @returns {Promise<{sections: {item_id:number, product_name:string, images:string[]}[]}>}
   */
  getPrintListImages(orderId, layout = 'zalo') {
    // Lần vẽ đầu (chưa có cache) của đơn CTV còn tải ảnh demo từ site CTV — nới hơn 30s mặc định.
    return apiClient
      .get(`/orders/${orderId}/print-list-images`, { params: { layout }, timeout: 120000 })
      .then(extractData);
  },

  // Ảnh in demo gắn theo TỪNG danh sách in (mỗi sản phẩm trong đơn một danh sách).
  uploadItemDemoImages(orderId, itemId, formData, options = {}) {
    return apiClient.post(`/orders/${orderId}/items/${itemId}/demo-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
      ...options,
    }).then(extractData);
  },

  deleteItemDemoImageByName(orderId, itemId, filename) {
    return apiClient
      .delete(`/orders/${orderId}/items/${itemId}/demo-image/${encodeURIComponent(filename)}`)
      .then(extractData);
  },


  listTags() {
    return apiClient.get('/orders/tags').then(extractData);
  },

  createTag(name, parentId = null) {
    const data = { name };
    if (parentId) data.parent_id = parentId;
    return apiClient.post('/orders/tags', data).then(extractData);
  },

  deleteTag(tagId) {
    return apiClient.delete(`/orders/tags/${tagId}`).then(extractData);
  },

  /**
   * Danh sách đánh giá của khách (khách tự gửi ở trang công khai /danh-gia/{code}).
   * Chỉ có GET — CMS không sửa/xoá đánh giá.
   * @param {Object} params - { page, page_size, rating_min, rating_max,
   *                            date_from, date_to, sort_by, sort_dir }
   * @returns {Promise<Object>} { items, total, summary: { total, avg_rating, low_rating_count } }
   */
  listReviews(params) {
    return apiClient.get('/orders/reviews', { params }).then(extractData);
  },
};
